import {
  subclass,
  property
} from "@arcgis/core/core/accessorSupport/decorators";

import PortalBase from  "@arcgis/core/portal/Portal";
import esriRequest from "@arcgis/core/request";
import { getLocale } from '@arcgis/core/intl';

interface Portal extends PortalBase {}

@subclass("esri.portal.Portal")
class Portal extends PortalBase {
  @property()
  public credential = null;

  @property()
  public subscriptionInfo = null;

  @property()
  public allowedBetaFeatures = [];

  constructor(params?: any) {
    super(params);
  }

  public isUniqueItemTitle(
    title: string,
    folder: string,
    type: string
  ): Promise<boolean> {
    const { user } = this;
    const queryParams = {
      query:
        "owner:" +
        user.username +
        " ownerfolder:" +
        (folder || "root") +
        ' type:"' +
        type +
        "\" title:'" +
        title +
        "'",
      num: 100
    };
    return this.queryItems(queryParams).then(results => {
      const items = results.total ? results.results : [];
      const matches = items.some(item => item.title === title);
      return !matches;
    });
  }

  public getUniqueItemTitle(
    suggestedTitle: string,
    folder: string,
    type: string
  ): Promise<string> {
    const { user } = this;
    const queryParams = {
      query:
        "owner:" +
        user.username +
        " orgid:" +
        this.id +
        " ownerfolder:" +
        (folder || "root") +
        ' type:"' +
        type +
        "\" title:'" +
        suggestedTitle +
        "'",
      num: 100
    };
    return this.queryItems(queryParams).then(results => {
      return results && results.total > 0
        ? suggestedTitle + "_" + (results.total + 1)
        : suggestedTitle;
    });
  }

  getRegion(): string {
    const portalRegion = this.region;
    const user = this.user;
    const userRegion = user && user.region;
    const ipCntryCode = this.ipCntryCode;
    const jsApiLocale = getLocale() || "US";
    return userRegion || portalRegion || ipCntryCode || jsApiLocale;
  }

  getUnits(): string {
    const portalRegion = (this.region || "").toLowerCase();
    const portalUnits = this.units;
    const user = this.user;
    const userUnits = user && user.units;
    const userRegion = user && (user.region || "").toLowerCase();
    const ipCntryCode = (this.ipCntryCode || "").toLowerCase();
    const dojoLocale = (getLocale() || "").toLowerCase();
    // if user doesnt have units, portal doesnt have units, and all regions are US then use "english"
    const useEnglish =
      userRegion === "us" ||
      portalRegion === "us" ||
      ipCntryCode === "us" ||
      dojoLocale.indexOf("us") > -1;
    return userUnits || portalUnits || useEnglish ? "english" : "metric";
  }

  getWorldLocator(): string {
    const user = this.user as any;
    // const canGeocode = !user || (user && user.userRole && user.userRole.canUseGeocode());
    const locators = this.helperServices.geocode;
    const worldGeocoderRegEx = /(arcgis.com\/arcgis\/rest\/services\/world\/geocodeserver).*/gi;
    const worldLocators = locators.filter(locator => {
      return locator.url.match(worldGeocoderRegEx);
    });
    return worldLocators && worldLocators.length ? worldLocators.shift() : null;
  }

  public deleteItems(items: any): Promise<any> {
    const user = this.user;
    const contentUrl = this.user.userContentUrl + "/deleteItems";
    const deleteItems =
      items && items.length
        ? items.map(item => {
            return item.id;
          })
        : [];
    if (deleteItems && deleteItems.length) {
      return esriRequest(contentUrl, {
        query: { items: deleteItems.join(","), f: "json" },
        responseType: "json",
        method: "post"
      });
    } else {
      return Promise.resolve(true);
    }
  }

  public addItem(params: any): Promise<any> {
    const folder = params && (params.item.folderId || params.item.ownerFolder);
    const data = params && params.data;
    const item = params && params.item;
    let url = this.user.userContentUrl;
    if (folder) {
      url += "/" + folder;
    }
    return esriRequest(url + "/addItem", {
      query: { ...item, text: data, async: true, overwrite: false, f: "json" },
      responseType: "json",
      method: "post"
    });
  }

  public fetchNotifications(): Promise<any> {
    const { user } = this;
    if (user) {
      const url = user.get("url") + "/notifications";
      return esriRequest(url, {
        query: { f: "json" },
        responseType: "json",
        method: "post"
      }).then(result => {
        const notifications =
          (result && result.data && result.data.notifications) || [];
        return notifications.map(this.formatNotification.bind(this));
      });
    } else {
      Promise.resolve([]);
    }
  }

  // TODO
  private formatNotification(notification) {
    return notification;
  }
}

export default Portal;
